import { type ReactElement, useMemo } from "react";

import { DropdownMenu } from "@radix-ui/themes";

import { useGetTradingAccount } from "~/v2/api/hooks";
import { WidgetOverflowMenuItems } from "~/v3-poc/components/Widget";
import { WatchlistAddSearch } from "./WatchlistAddSearch.connector";
import { WatchlistNameDialog } from "./WatchlistNameDialog.connector";
import { WatchlistSetupPresentation } from "./WatchlistSetup.pres";
import { useWatchlistWidgetController } from "./WatchlistWidget.controller";
import { WatchlistWidgetPresentation } from "./WatchlistWidget.pres";

export interface WatchlistWidgetProps {
  id: string;
}

/**
 * Binds a workspace widget to a gobroker watchlist. `widget.watchlistId` is the
 * only persisted state — name and membership always come from the detail query,
 * so two widgets on the same list share one cache entry and stay in step.
 *
 * Publishing rules:
 * - Grouped: a row click calls `setGroupSymbol(groupId, symbol)`, which
 *   atomically clears incompatible option legs in the group.
 * - Ungrouped: a row click calls `setWidgetLocalSymbol(id, symbol)` — local only.
 * - Clicking the selected row again clears the effective symbol.
 */
export const WatchlistWidget = ({ id }: WatchlistWidgetProps): ReactElement => {
  const { account } = useGetTradingAccount();
  const { view, showRenameAction, onRename, nameDialog } =
    useWatchlistWidgetController({ id, accountId: account?.id });

  const overflowMenuItems = useMemo(
    () =>
      showRenameAction ? (
        <WidgetOverflowMenuItems>
          <DropdownMenu.Item onSelect={onRename}>
            Rename watchlist
          </DropdownMenu.Item>
        </WidgetOverflowMenuItems>
      ) : null,
    [showRenameAction, onRename],
  );

  const nameDialogNode =
    nameDialog !== null ? <WatchlistNameDialog {...nameDialog} /> : undefined;

  if (view.status === "setup") {
    return (
      <WatchlistWidgetPresentation
        status="setup"
        setup={<WatchlistSetupPresentation {...view.setup} />}
        overflowMenuItems={overflowMenuItems}
        nameDialog={nameDialogNode}
      />
    );
  }

  if (view.status === "loading") {
    return (
      <WatchlistWidgetPresentation
        status="loading"
        overflowMenuItems={overflowMenuItems}
        nameDialog={nameDialogNode}
      />
    );
  }

  if (view.status === "error") {
    return (
      <WatchlistWidgetPresentation
        status="error"
        onRetry={view.onRetry}
        overflowMenuItems={overflowMenuItems}
        nameDialog={nameDialogNode}
      />
    );
  }

  return (
    <WatchlistWidgetPresentation
      status="bound"
      rows={view.rows}
      addSearch={<WatchlistAddSearch {...view.addSearch} />}
      groupSymbolError={view.groupSymbolError}
      onToggle={view.onToggle}
      overflowMenuItems={overflowMenuItems}
      nameDialog={nameDialogNode}
    />
  );
};
